import clsx from "clsx";
import type { BrandingWatermarkPosition } from "@/services/api/agentic-v2/types";

const POSITIONS: BrandingWatermarkPosition[] = [
  "top_left",
  "top_center",
  "top_right",
  "center_left",
  "center",
  "center_right",
  "bottom_left",
  "bottom_center",
  "bottom_right",
];

const LABELS: Record<BrandingWatermarkPosition, string> = {
  top_left: "Top left",
  top_center: "Top center",
  top_right: "Top right",
  center_left: "Center left",
  center: "Center",
  center_right: "Center right",
  bottom_left: "Bottom left",
  bottom_center: "Bottom center",
  bottom_right: "Bottom right",
};

export function BrandingPlacementPicker({
  value,
  onChange,
  disabled = false,
  showLabel = true,
}: {
  value: BrandingWatermarkPosition;
  onChange: (position: BrandingWatermarkPosition) => void;
  disabled?: boolean;
  showLabel?: boolean;
}) {
  const grid = (
    <div
      className="inline-grid grid-cols-3 gap-2"
      role="group"
      aria-label="Logo position"
    >
      {POSITIONS.map((position) => (
        <button
          key={position}
          type="button"
          disabled={disabled}
          aria-label={LABELS[position]}
          aria-pressed={value === position}
          title={LABELS[position]}
          data-testid={`branding-placement-${position}`}
          className={clsx(
            "size-8 shrink-0 rounded-md border-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900",
            value === position
              ? "bg-indigo-500"
              : "bg-slate-300 hover:bg-slate-400 dark:bg-slate-600 dark:hover:bg-slate-500",
            disabled && "cursor-not-allowed opacity-50",
          )}
          onClick={() => onChange(position)}
        />
      ))}
    </div>
  );

  if (!showLabel) {
    return (
      <div data-testid="branding-placement-picker">{grid}</div>
    );
  }

  return (
    <div className="space-y-2" data-testid="branding-placement-picker">
      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Logo position</p>
      {grid}
    </div>
  );
}

export function watermarkPositionClasses(
  position: BrandingWatermarkPosition,
  variant: "desktop" | "preview" = "desktop",
): string {
  if (variant === "preview") {
    switch (position) {
      case "top_left":
        return "top-3 left-3";
      case "top_center":
        return "top-3 left-1/2 -translate-x-1/2";
      case "top_right":
        return "top-3 right-3";
      case "center_left":
        return "top-1/2 left-3 -translate-y-1/2";
      case "center":
        return "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";
      case "center_right":
        return "top-1/2 right-3 -translate-y-1/2";
      case "bottom_left":
        return "bottom-4 left-3";
      case "bottom_center":
        return "bottom-4 left-1/2 -translate-x-1/2";
      case "bottom_right":
      default:
        return "bottom-4 right-3";
    }
  }

  switch (position) {
    case "top_left":
      return "top-8 left-8";
    case "top_center":
      return "top-8 left-1/2 -translate-x-1/2";
    case "top_right":
      return "top-8 right-8";
    case "center_left":
      return "top-1/2 left-8 -translate-y-1/2";
    case "center":
      return "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";
    case "center_right":
      return "top-1/2 right-8 -translate-y-1/2";
    case "bottom_left":
      return "bottom-8 left-8";
    case "bottom_center":
      return "bottom-8 left-1/2 -translate-x-1/2";
    case "bottom_right":
    default:
      return "bottom-8 right-8";
  }
}
